/**
 * The profession templates (FR-C6, ADR-0014 §1).
 *
 * **The preview creates nothing.** Everything on this screen is read-only
 * until the firm presses Import, and that is not an implementation detail —
 * it is the requirement, so the screen says it out loud.
 *
 * All four professions are offered, not only the firm's own: a chartered
 * accountant who also files with the ROC is not doing something odd, and
 * hiding the company-secretarial template from them would make the product
 * narrower than the practice.
 *
 * The screen also says what the templates deliberately leave out — no fee, no
 * SAC code, no due date — because a firm that imports fifteen services and
 * finds every fee blank should know that was a decision rather than a gap.
 *
 * **Wave 15F** (C14): a list under the catalog — the kit's `Tabs` choose the
 * profession, the template's services are the kit's `DataTable` with a tick per
 * row and a search, and *Import N services* is the page's one primary action.
 */

import { Alert, Button, Card, CardBody, Checkbox, Tabs } from '@fiveox/ui'
import { Check, Download } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { ListPage, ListTable, ListToolbar, useListTable } from '../../components/page'
import type { ListColumn } from '../../components/page'
import { SERVICE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type TemplatePreview,
  type TemplateService,
  type TemplateSummary,
  appliesToLabel,
  defaultTemplate,
  services,
} from '../../lib/api/services'

export function Templates() {
  const { can } = useEvaluation()
  const importable = can(SERVICE, 'can_create')
  const navigate = useNavigate()

  const [summaries, setSummaries] = useState<TemplateSummary[]>([])
  const [profession, setProfession] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    const body = await services.templates()
    setSummaries(body.data.templates)
    setProfession(body.data.profession)
    // The firm's own profession if we know it, **and otherwise the first one**.
    // Selecting nothing left this screen as four buttons over an empty page for
    // every firm whose profession is unset — which is every firm that has not
    // finished onboarding, and they are exactly the firms this screen is for.
    // Found by opening it as a real user (CLAUDE.md §8 rule 1).
    const first = defaultTemplate(body.data.templates)
    setChosen(first)
    if (!first) setLoading(false)
  }, [])

  useEffect(() => {
    void load().catch(() => {
      setError('The templates could not be loaded.')
      setLoading(false)
    })
  }, [load])

  useEffect(() => {
    if (!chosen) return
    setLoading(true)
    void services
      .template(chosen)
      .then((body) => {
        setPreview(body.data)
        setSelected(new Set(body.data.services.map((s) => s.slug)))
      })
      .catch(() => setError('That template could not be loaded.'))
      .finally(() => setLoading(false))
  }, [chosen])

  const runImport = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      const all = selected.size === preview.services.length
      const body = await services.importTemplate(preview.profession, all ? null : [...selected])
      setResult({ created: body.data.created.length, skipped: body.data.skipped.length })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The import did not run.')
    } finally {
      setBusy(false)
    }
  }

  const toggle = useCallback((slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }, [])

  const columns = useMemo<ListColumn<TemplateService>[]>(
    () => [
      {
        key: 'pick',
        label: 'Import',
        width: 70,
        phone: true,
        render: (service) => (
          <Checkbox
            aria-label={`Import ${service.name}`}
            checked={selected.has(service.slug)}
            disabled={!importable}
            onChange={() => toggle(service.slug)}
          />
        ),
      },
      {
        key: 'name',
        label: 'Service',
        width: 260,
        required: true,
        phone: true,
        sortField: 'name',
        render: (service) => (
          <span className="block min-w-0">
            <span className="text-foreground font-medium">{service.name}</span>
            <span className="text-muted-foreground block text-xs">{service.description}</span>
          </span>
        ),
      },
      {
        key: 'for',
        label: 'For',
        width: 150,
        render: (service) => (
          <span className="text-muted-foreground">{appliesToLabel(service.applies_to)}</span>
        ),
      },
      {
        key: 'workflow',
        label: 'Workflow',
        width: 180,
        render: (service) => (
          <span className="text-muted-foreground">
            {service.workflow} · {service.stage_count} stages
          </span>
        ),
      },
      {
        key: 'plans',
        label: 'Plans',
        width: 160,
        render: (service) => (
          <span className="text-muted-foreground">{service.plans.map((p) => p.name).join(', ')}</span>
        ),
      },
    ],
    [selected, importable, toggle],
  )

  const list = useListTable(preview?.services ?? [], {
    columns,
    search: (service) => [service.name, service.category, service.description],
  })

  const importButton =
    importable && preview && !result ? (
      <Button
        onClick={() => void runImport()}
        disabled={busy || selected.size === 0}
        title={selected.size === 0 ? 'Tick at least one service to import.' : undefined}
        loading={busy}
        loadingText="Importing…"
      >
        <Download size={16} aria-hidden /> Import {selected.size} service
        {selected.size === 1 ? '' : 's'}
      </Button>
    ) : undefined

  return (
    <ListPage
      back={{ to: '/services', label: 'Services' }}
      title="Start from a template"
      description="A starting catalog for each profession — services, plans, document checklists and a workflow for each. Everything here is yours to keep, edit or discard."
      primary={importButton}
      loading={loading && !result}
      error={error}
      isEmpty={false}
      empty={{ title: 'No templates', description: 'ProDesk ships no templates.' }}
    >
      {/* w13b — ADR-0031 §5: nothing is shipped for `other`. */}
      {profession === 'other' && !result && (
        <Alert tone="info">
          ProDesk ships no starting catalog for your kind of practice —{' '}
          <Link to="/services" className="font-medium underline">
            add your own services
          </Link>
          . The four below are for CA, CS, CMA and advocate practices; take anything that fits.
        </Alert>
      )}

      {summaries.length > 0 && !summaries.some((t) => t.is_yours) && profession !== 'other' && !result && (
        <Alert tone="info">
          Your firm has not recorded a profession yet, so none of these is marked as yours. Pick the
          one that fits — or set it in{' '}
          <Link to="/onboarding" className="font-medium underline">
            onboarding
          </Link>
          .
        </Alert>
      )}

      {result ? (
        <Card>
          <CardBody className="space-y-3">
            <p className="text-foreground text-base font-medium">
              {result.created} service{result.created === 1 ? '' : 's'} added
              {result.skipped > 0 && `, ${result.skipped} you already had left alone`}.
            </p>
            <p className="text-muted-foreground text-sm">
              Every plan came in without a fee. Setting them is the next thing to do.
            </p>
            <Button onClick={() => navigate('/services')}>Open the catalog</Button>
          </CardBody>
        </Card>
      ) : (
        <>
          {summaries.length > 0 && (
            <Tabs
              idBase="template"
              active={chosen ?? ''}
              onChange={setChosen}
              tabs={summaries.map((summary) => ({
                id: summary.profession,
                label: summary.is_yours ? `${summary.label} · yours` : summary.label,
              }))}
            />
          )}

          {preview && (
            <div className="space-y-4">
              <Alert tone="info">
                <span className="text-foreground font-medium">{preview.label}.</span> {preview.note}{' '}
                Nothing is created until you import.
              </Alert>
              <ListToolbar list={list} placeholder="Search this template" />
              <ListTable list={list} rowKey={(service) => service.slug} />
              {!importable && (
                <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <Check size={14} aria-hidden /> You can read this template. Adding services to your
                  firm's catalog is a partner's or practice manager's action.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </ListPage>
  )
}
